import { Box }                                        from '@chakra-ui/react';
import { Grid }                                       from '@chakra-ui/react';
import { GridItem }                                   from '@chakra-ui/react';
import { Input }                                      from '@chakra-ui/react';
import { InputGroup }                                 from '@chakra-ui/react';
import { Text }                                       from '@chakra-ui/react';
import { Controller }                                 from 'react-hook-form';
import type { Control }                               from 'react-hook-form';
import type { FieldArrayWithId }                      from 'react-hook-form';
import type { FieldErrors }                           from 'react-hook-form';
import type { UseFormRegister }                       from 'react-hook-form';
import { SelectContent }                              from '@/components/ui/select';
import { SelectItem }                                 from '@/components/ui/select';
import { SelectRoot }                                 from '@/components/ui/select';
import { SelectTrigger }                              from '@/components/ui/select';
import { SelectValueText }                            from '@/components/ui/select';
import type { ListCollection }                        from '@chakra-ui/react';
import { useTranslation }                             from 'react-i18next';
import type { FormValues }                            from '../hooks/useEditStandForm';

interface ISocialNetworksSectionProps {
	control: Control<FormValues>;
	register: UseFormRegister<FormValues>;
	errors: FieldErrors<FormValues>;
	fields: FieldArrayWithId<FormValues, 'socialNetworks', 'id'>[];
	linkTypes: ListCollection<{ label: string; value: string }>;
	onAdd: () => void;
	onRemove: (index: number) => void;
	isDisabled?: boolean;
}

const SocialNetworksSection = ({ control, register, errors, fields, linkTypes, onAdd, onRemove, isDisabled }: ISocialNetworksSectionProps) => {
	const { t } = useTranslation();

	return (
		<>
			<Text className='poppins-bold' textStyle="lg" pt="60px" pb="20px">
				{t('pages.editStand.form.socialNetworks.title')}
			</Text>
			<Grid templateColumns="repeat(2, 1fr)" mb="10px">
				<GridItem colSpan={1}>
					<Text className='poppins-semibold'>{t('pages.editStand.form.socialNetworks.socialNetworkType.title')}</Text>
				</GridItem>
				<GridItem colSpan={1}>
					<Text className='poppins-semibold'>{t('pages.editStand.form.socialNetworks.linkValue.title')}</Text>
				</GridItem>
			</Grid>
			{fields.map((field, index) => (
				<Grid key={field.id} templateColumns="repeat(2, 1fr)" mb="10px">
					<GridItem colSpan={1}>
						<Controller
							control={control}
							name={`socialNetworks.${index}.linkTypeUrn` as `socialNetworks.${number}.linkTypeUrn`}
							render={({ field: controllerField }) => (
								<SelectRoot
									collection={linkTypes}
									value={controllerField.value ? [controllerField.value as string] : []}
									onValueChange={v => controllerField.onChange(v.value[0] ?? '')}
									disabled={isDisabled}
								>
									<SelectTrigger cursor={isDisabled ? 'default' : 'pointer'} marginRight="20px">
										<SelectValueText placeholder={t('pages.editStand.form.socialNetworks.socialNetworkType.placeholder')} />
									</SelectTrigger>
									<SelectContent>
										{linkTypes.items.map((lt) => (
											<SelectItem
												cursor="pointer"
												item={lt}
												key={lt.value}
												_hover={{ backgroundColor: '#DEDEDE' }}
											>
												{lt.label}
											</SelectItem>
										))}
									</SelectContent>
								</SelectRoot>
							)}
						/>
					</GridItem>
					<GridItem colSpan={1}>
						<Box display="flex" flexDirection="column" gap="4px">
						<Box display="flex" gap="10px" alignItems="center">
							<InputGroup startElement="https://" startElementProps={{ color: 'fg.muted' }}>
								<Input
									ps="7ch"
									placeholder={t('pages.editStand.form.socialNetworks.linkValue.placeholder')}
									borderColor={errors.socialNetworks?.[index]?.url ? 'red.500' : undefined}
									disabled={isDisabled}
									{...register(`socialNetworks.${index}.url`)}
								/>
							</InputGroup>
							{!isDisabled && fields.length > 1 && (
								<Box
									as="button"
									onClick={() => onRemove(index)}
									width="28px"
									height="28px"
									minWidth="28px"
									borderRadius="full"
									border="1.5px solid"
									borderColor="gray.400"
									display="flex"
									alignItems="center"
									justifyContent="center"
									cursor="pointer"
									_hover={{ borderColor: 'gray.600' }}
								>
									<Box width="10px" height="1.5px" backgroundColor="gray.600" />
								</Box>
							)}
						</Box>
						{errors.socialNetworks?.[index]?.url && (
							<Text textStyle="xs" color="red.500">
								{errors.socialNetworks[index]?.url?.message as string}
							</Text>
						)}
						</Box>
					</GridItem>
				</Grid>
			))}
			{!isDisabled && (
				<Text
					as="button"
					fontWeight={500}
					onClick={onAdd}
					textDecoration="underline"
					cursor="pointer"
					textStyle="sm"
					mt="10px"
					alignSelf="flex-start"
				>
					{t('pages.editStand.form.socialNetworks.addSocialNetwork')}
				</Text>
			)}
		</>
	);
};

export default SocialNetworksSection;
